import { useState, useRef, useEffect, useCallback, useId, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiChevronUp, FiSearch } from "react-icons/fi";

// Soft cap on how tall the dropdown is allowed to get — matches the old
// `max-h-60` Tailwind class, but now applied dynamically so it can also
// shrink further when the available space (above or below) is tighter.
const DROPDOWN_MAX_HEIGHT = 240;
const GAP = 4;

const CustomSelect = ({
    options = [],
    value,
    defaultValue,
    onChange,
    placeholder = "Select...",
    isDisabled = false,
    name,
    fontSize = "text-sm",
    isSearchable = false,
    searchPlaceholder = "Search...",
}) => {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [searchText, setSearchText] = useState("");
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const optionRefs = useRef([]);
    const listboxId = useId();

    const allOptions = useMemo(
        () => (Array.isArray(options) ? options.filter(Boolean) : []),
        [options]
    );

    // The searchable list is filtered by searchText; everything below keyboard
    // nav / open-position logic operates on this, so filtering "just works".
    const safeOptions = useMemo(() => {
        if (!isSearchable || !searchText.trim()) return allOptions;
        const q = searchText.trim().toLowerCase();
        return allOptions.filter((opt) => opt.label?.toLowerCase().includes(q));
    }, [allOptions, isSearchable, searchText]);

    // NEW: fall back to internalValue when `value` isn't controlled
    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;

    // Only show the default value if it actually exists in options
    const selected = allOptions.find((opt) => opt.value === currentValue);

    const findSelectableIndex = useCallback(
        (startIdx, direction) => {
            let idx = startIdx;
            while (idx >= 0 && idx < safeOptions.length) {
                if (!safeOptions[idx]?.isDisabled) return idx;
                idx += direction;
            }
            return -1;
        },
        [safeOptions]
    );

    const closeDropdown = useCallback(() => {
        setOpen(false);
        setActiveIndex(-1);
        setSearchText("");
    }, []);

    // Decides whether the dropdown should open below or above the trigger,
    // and how tall it's allowed to be, based on actual remaining viewport
    // space on each side. `measuredHeight` is the dropdown's real rendered
    // height when known (post-mount); before mount we fall back to the
    // soft cap so the very first guess is already usually correct.
    const computePosition = useCallback((measuredHeight) => {
        const trigger = triggerRef.current;
        if (!trigger) return null;

        const rect = trigger.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom - GAP;
        const spaceAbove = rect.top - GAP;

        const desiredHeight = Math.min(measuredHeight ?? DROPDOWN_MAX_HEIGHT, DROPDOWN_MAX_HEIGHT);
        const fitsBelow = spaceBelow >= desiredHeight;
        // Flip above only when it doesn't fit below AND there's more room above.
        const openAbove = !fitsBelow && spaceAbove > spaceBelow;

        const maxHeight = Math.max(
            0,
            Math.min(DROPDOWN_MAX_HEIGHT, openAbove ? spaceAbove : spaceBelow)
        );

        return {
            position: "fixed",
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
            maxHeight,
            ...(openAbove
                ? { bottom: viewportHeight - rect.top + GAP }
                : { top: rect.bottom + GAP }),
        };
    }, []);

    const updatePosition = useCallback(() => {
        // Once mounted, use the dropdown's real height for an exact fit;
        // otherwise (first open, pre-mount) estimate with the soft cap.
        const measured = dropdownRef.current?.offsetHeight;
        const style = computePosition(measured);
        if (style) setDropdownStyle(style);
    }, [computePosition]);

    const openDropdown = useCallback(() => {
        const selectedIdx = allOptions.findIndex((opt) => opt.value === currentValue);
        const seedIdx =
            selectedIdx >= 0 && !allOptions[selectedIdx]?.isDisabled
                ? selectedIdx
                : findSelectableIndex(0, 1);
        setActiveIndex(seedIdx >= 0 ? seedIdx : -1);
        updatePosition();
        setOpen(true);
    }, [allOptions, currentValue, findSelectableIndex, updatePosition]);

    // Refines position right after the dropdown actually mounts (or its
    // content/height changes), using its real measured height — this is
    // what corrects an initial "below" guess into "above" once we know
    // exactly how tall the list turned out to be. useLayoutEffect runs
    // before paint, so there's no visible flicker.
    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, safeOptions.length]);

    // Focus the search box as soon as the dropdown opens.
    useEffect(() => {
        if (open && isSearchable) {
            searchInputRef.current?.focus();
        }
    }, [open, isSearchable]);

    // Derived (not stored) active index: `activeIndex` is the last index the
    // user explicitly navigated/hovered to, but it can point past the end of
    // the list — or at a now-filtered-out option — once the list is filtered
    // by search. Rather than syncing that with a setState-in-effect (which
    // causes an extra cascading render), we just compute a valid fallback
    // during render. This is a pure derivation of existing state + props,
    // so useMemo is the right tool, not an effect.
    const effectiveActiveIndex = useMemo(() => {
        if (activeIndex >= 0 && activeIndex < safeOptions.length && !safeOptions[activeIndex]?.isDisabled) {
            return activeIndex;
        }
        return findSelectableIndex(0, 1);
    }, [activeIndex, safeOptions, findSelectableIndex]);

    useEffect(() => {
        if (!open) return;
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (e) => {
            const clickedTrigger = triggerRef.current?.contains(e.target);
            const clickedDropdown = dropdownRef.current?.contains(e.target);
            if (!clickedTrigger && !clickedDropdown) {
                closeDropdown();
            }
        };

        const handleKeyDown = (e) => {
            switch (e.key) {
                case "Escape":
                    e.preventDefault();
                    closeDropdown();
                    triggerRef.current?.focus();
                    break;
                case "ArrowDown": {
                    e.preventDefault();
                    const next = findSelectableIndex(
                        Math.min(activeIndex + 1, safeOptions.length - 1),
                        1
                    );
                    if (next !== -1) setActiveIndex(next);
                    break;
                }
                case "ArrowUp": {
                    e.preventDefault();
                    const prev = findSelectableIndex(Math.max(activeIndex - 1, 0), -1);
                    if (prev !== -1) setActiveIndex(prev);
                    break;
                }
                case "Enter":
                    e.preventDefault();
                    if (
                        effectiveActiveIndex >= 0 &&
                        safeOptions[effectiveActiveIndex] &&
                        !safeOptions[effectiveActiveIndex].isDisabled
                    ) {
                        const nextVal = safeOptions[effectiveActiveIndex].value;
                        if (!isControlled) setInternalValue(nextVal); // NEW
                        onChange?.(nextVal);
                        closeDropdown();
                        triggerRef.current?.focus();
                    }
                    break;
                case " ":
                    // Space should type into the search box instead of selecting.
                    if (!isSearchable) {
                        e.preventDefault();
                        if (
                            effectiveActiveIndex >= 0 &&
                            safeOptions[effectiveActiveIndex] &&
                            !safeOptions[effectiveActiveIndex].isDisabled
                        ) {
                            const nextVal = safeOptions[effectiveActiveIndex].value;
                            if (!isControlled) setInternalValue(nextVal);
                            onChange?.(nextVal);
                            closeDropdown();
                            triggerRef.current?.focus();
                        }
                    }
                    break;
                case "Tab":
                    closeDropdown();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, activeIndex, effectiveActiveIndex, safeOptions, onChange, closeDropdown, findSelectableIndex, isControlled, isSearchable]);

    useEffect(() => {
        if (!open) return;
        const el = optionRefs.current[effectiveActiveIndex];
        if (el) {
            el.scrollIntoView({ block: "nearest" });
        }
    }, [effectiveActiveIndex, open]);

    const toggleOpen = (e) => {
        if (isDisabled) return;
        e.stopPropagation();
        if (open) {
            closeDropdown();
        } else {
            openDropdown();
        }
    };

    const handleTriggerKeyDown = (e) => {
        if (isDisabled) return;
        if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
            e.preventDefault();
            openDropdown();
        }
    };

    const selectOption = (opt) => {
        if (opt.isDisabled) return;
        if (!isControlled) setInternalValue(opt.value); // NEW
        onChange?.(opt.value);
        closeDropdown();
    };

    return (
        <>
            <div
                ref={triggerRef}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                onClick={toggleOpen}
                onKeyDown={handleTriggerKeyDown}
                data-name={name}
                className={`relative flex items-center justify-between w-full px-2 py-1 border rounded-md h-[34px] outline-none
                    ${isDisabled
                        ? "border-gray-200 bg-gray-50 cursor-not-allowed text-gray-400"
                        : "border-gray-300 bg-white cursor-pointer focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                    }`}
            >
                <span
                    className={`truncate ${fontSize} ${selected ? (isDisabled ? "text-gray-400" : "text-gray-800") : "text-gray-400"
                        }`}
                >
                    {selected ? selected.label : placeholder}
                </span>
                {open ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
            </div>

            {open &&
                !isDisabled &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        style={{ ...dropdownStyle, maxHeight: undefined }}
                        className="bg-white border border-gray-300 rounded-md shadow-md flex flex-col"
                    >
                        {isSearchable && (
                            <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-200 shrink-0">
                                <FiSearch className="text-gray-400 shrink-0" size={14} />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder={searchPlaceholder}
                                    className={`flex-1 min-w-0 outline-none bg-transparent text-gray-800 placeholder:text-gray-400 ${fontSize}`}
                                />
                            </div>
                        )}

                        <div
                            id={listboxId}
                            role="listbox"
                            style={{ maxHeight: dropdownStyle.maxHeight }}
                            className={`overflow-auto ${fontSize} tiny-scrollbar`}
                        >
                            {safeOptions.length === 0 ? (
                                <div className={`px-3 py-2 text-gray-400 ${fontSize}`}>
                                    {isSearchable && searchText.trim() ? "No matches found" : "No options available"}
                                </div>
                            ) : (
                                safeOptions.map((opt, idx) => (
                                    <div
                                        key={opt.value}
                                        ref={(el) => (optionRefs.current[idx] = el)}
                                        role="option"
                                        aria-selected={currentValue === opt.value}
                                        aria-disabled={opt.isDisabled || undefined}
                                        onMouseEnter={() => {
                                            if (!opt.isDisabled) setActiveIndex(idx);
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            selectOption(opt);
                                        }}
                                        className={`px-2 py-1.5 truncate
                                            ${opt.isDisabled
                                                ? "text-gray-300 cursor-not-allowed"
                                                : "cursor-pointer text-gray-800"
                                            }
                                            ${!opt.isDisabled && currentValue === opt.value ? "bg-blue-100 text-blue-700" : ""}
                                            ${!opt.isDisabled && effectiveActiveIndex === idx && currentValue !== opt.value ? "bg-blue-50" : ""}
                                        `}
                                    >
                                        {opt.label}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default CustomSelect;